import { FC, ReactNode } from 'react';
import styles from './PunchupPageControls.module.css';

interface PunchupPageControlsProps {
	/**
	 * Left slot — the data-narrowing text input. Always a `PunchupSearch`. The
	 * search bar grows to fill available row width so it always feels filled
	 * on narrow viewports.
	 */
	search: ReactNode;
	/**
	 * Right slot — narrowing controls that toggle WHICH data is shown:
	 * filter dropdowns (e.g. "All Time" period select) and column pickers
	 * (Columns, Custom Questions). Optional. Each control inside should use
	 * the icon-collapse pattern when appropriate so the row stays compact on
	 * mobile.
	 *
	 * Page-level affordances such as Download, Refresh, and Send are NOT in
	 * this slot — they live in `PunchupTablePageHeader` above the controls
	 * row to keep the visual hierarchy consistent across table pages.
	 */
	actions?: ReactNode;
}

/**
 * Search + narrowing-controls row. Sits between `PunchupTablePageHeader` and
 * the table itself, inside `PunchupTableLayout` so the right edge of the
 * actions group aligns with the right edge of the table on wide viewports.
 *
 * Layout (any viewport): single horizontal row — search grows to fill the
 * slack via `flex: 1 1 auto`; actions group stays at natural width on the
 * right. The search input fills the available slot at every width, so the
 * row always feels filled rather than leaving a wide gap between the input
 * and the action buttons.
 *
 * The action controls themselves opt-in to icon-only mode at narrow viewports
 * via their own className (e.g. `PunchupColumnPicker` / `PunchupFilterSelect`
 * may collapse to icon-only). No vertical stacking — page-level affordances
 * (Download / Refresh / custom CTA) live on `PunchupTablePageHeader` (via
 * `flex-wrap`) where there are more buttons; this row only carries
 * search + narrowing controls.
 */
export const PunchupPageControls: FC<PunchupPageControlsProps> = ({ search, actions }) => (
	<div className={styles.root}>
		<div className={styles.search}>{search}</div>
		{actions && <div className={styles.actions}>{actions}</div>}
	</div>
);
